import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Wifi, WifiOff, Globe, Signal, Zap,
  RefreshCw, ChevronDown, ChevronUp, Plus, X, Search,
  Clock, AlertCircle, CheckCircle, FileText, Phone, Mail,
  Shield, UserPlus, Lock, Settings, UserCheck, UserX, Pencil, Timer, Trash2, Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate and format downtime with live seconds
const calculateDowntime = (createdAt, resolvedAt = null) => {
  const startTime = new Date(createdAt);
  const endTime = resolvedAt ? new Date(resolvedAt) : new Date();
  let diffMs = endTime - startTime;
  
  // Ensure positive difference
  if (diffMs < 0) diffMs = 0;
  
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
  
  // Format: DD:HH:MM:SS
  const pad = (n) => n.toString().padStart(2, '0');
  
  if (days > 0) {
    return `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  } else if (hours > 0) {
    return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  } else if (minutes > 0) {
    return `${pad(minutes)}m ${pad(seconds)}s`;
  } else {
    return `${pad(seconds)}s`;
  }
};

// Partner colors and icons
const PARTNER_CONFIG = {
  timbl: { color: '#3B82F6', bgColor: 'bg-blue-500', lightBg: 'bg-blue-50', textColor: 'text-blue-700', icon: Globe },
  bharti_airtel: { color: '#EF4444', bgColor: 'bg-red-500', lightBg: 'bg-red-50', textColor: 'text-red-700', icon: Signal },
  jio: { color: '#8B5CF6', bgColor: 'bg-purple-500', lightBg: 'bg-purple-50', textColor: 'text-purple-700', icon: Wifi },
  lco: { color: '#F59E0B', bgColor: 'bg-amber-500', lightBg: 'bg-amber-50', textColor: 'text-amber-700', icon: Building2 },
  netix_fibernet: { color: '#10B981', bgColor: 'bg-emerald-500', lightBg: 'bg-emerald-50', textColor: 'text-emerald-700', icon: Zap },
};

const PartnerDashboard = ({ refreshInterval = 30000 }) => {
  const [summary, setSummary] = useState(null);
  const [partnerCustomers, setPartnerCustomers] = useState({});
  const [serviceRequests, setServiceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showServiceRequestModal, setShowServiceRequestModal] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Service request form state
  const [requestForm, setRequestForm] = useState({
    customer_id: '',
    customer_name: '',
    partner_id: '',
    partner_name: '',
    circuit_id: '',
    customer_ip: '',
    first_troubleshoot: 'No',
    request_type: 'General',
    priority: 'Medium',
    subject: '',
    description: '',
    contact_name: ''
  });
  
  // Permission management state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [adminsWithPermission, setAdminsWithPermission] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [selectedAdminToGrant, setSelectedAdminToGrant] = useState('');
  const [updatingRequest, setUpdatingRequest] = useState(null);
  const [downtimeTick, setDowntimeTick] = useState(0); // For live downtime updates
  
  // Edit service request state
  const [editingRequest, setEditingRequest] = useState(null);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editRequestForm, setEditRequestForm] = useState({
    subject: '',
    description: '',
    request_type: 'General',
    priority: 'Medium',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    resolution_notes: ''
  });

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      if (!token) {
        setHasAccess(false);
        return false;
      }

      const response = await fetch(`${API_URL}/api/monitoring/partner/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHasAccess(true);
        setIsSuperAdmin(data.is_super_admin);
        return true;
      } else {
        setHasAccess(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
      return false;
    }
  };

  const fetchData = async (showToast = false) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('monitoringAdminToken');
      if (!token) return;

      // Fetch summary
      const summaryRes = await fetch(`${API_URL}/api/monitoring/partner/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      // Fetch customers by partner
      const customersRes = await fetch(`${API_URL}/api/monitoring/partner/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (customersRes.ok) {
        const customersData = await customersRes.json();
        setPartnerCustomers(customersData.partners || {});
      }

      // Fetch service requests
      const requestsRes = await fetch(`${API_URL}/api/monitoring/partner/service-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setServiceRequests(requestsData.requests || []);
      }

      setLastUpdate(new Date());
      if (showToast) toast.success('Partner data refreshed');

    } catch (error) {
      console.error('Error fetching partner data:', error);
      if (showToast) toast.error('Failed to fetch partner data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const hasAccessResult = await checkAccess();
      if (hasAccessResult) {
        await fetchData();
      }
      setLoading(false);
    };
    init();

    const interval = setInterval(() => {
      if (hasAccess) fetchData();
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, hasAccess]);

  // Live downtime ticker - updates every second for real-time display
  useEffect(() => {
    const downtimeInterval = setInterval(() => {
      setDowntimeTick(prev => prev + 1);
    }, 1000); // Update every second
    return () => clearInterval(downtimeInterval);
  }, []);

  const submitServiceRequest = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/service-request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestForm)
      });

      const data = await response.json();
      
      if (response.ok) {
        // Check if notification was sent
        if (data.notification?.sent) {
          toast.success('Service request created and notification sent to partner!', {
            duration: 5000,
            icon: '📧'
          });
        } else {
          toast.success('Service request created successfully');
          // Show info about notification if not sent
          if (data.notification?.message) {
            toast.info(data.notification.message, { duration: 4000 });
          }
        }
        setShowServiceRequestModal(false);
        setRequestForm({
          customer_id: '', customer_name: '', partner_id: '', partner_name: '',
          circuit_id: '', customer_ip: '', first_troubleshoot: 'No',
          request_type: 'General', priority: 'Medium', subject: '', description: '',
          contact_name: ''
        });
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to create service request');
      }
    } catch (error) {
      console.error('Error creating service request:', error);
      toast.error('Failed to create service request');
    }
  };

  // Update service request status
  const updateServiceRequestStatus = async (requestId, newStatus) => {
    try {
      setUpdatingRequest(requestId);
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/service-request/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Request marked as ${newStatus}`);
        fetchData();
      } else {
        toast.error('Failed to update request status');
      }
    } catch (error) {
      console.error('Error updating service request:', error);
      toast.error('Failed to update request status');
    } finally {
      setUpdatingRequest(null);
    }
  };

  // Delete service request
  const deleteServiceRequest = async (requestId, ticketNumber) => {
    if (!window.confirm(`Are you sure you want to delete ticket ${ticketNumber || requestId}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/service-request/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Service request deleted successfully');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete service request');
      }
    } catch (error) {
      console.error('Error deleting service request:', error);
      toast.error('Failed to delete service request');
    }
  };

  // Send reminder for open service request
  const [sendingReminder, setSendingReminder] = useState(null);
  
  const sendReminder = async (request) => {
    if (!window.confirm(`Send reminder email to ${request.partner_name} for ticket ${request.ticket_number}?`)) {
      return;
    }
    
    setSendingReminder(request.id);
    
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/service-request/${request.id}/reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.notification?.sent) {
          toast.success(`Reminder sent to ${request.partner_name}!`, {
            duration: 5000,
            icon: '🔔'
          });
        } else {
          toast.info(data.notification?.message || 'Reminder processed');
        }
      } else {
        toast.error(data.detail || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  // Open edit service request modal
  const openEditRequestModal = (request) => {
    setEditingRequest(request);
    setEditRequestForm({
      subject: request.subject || '',
      description: request.description || '',
      request_type: request.request_type || 'General',
      priority: request.priority || 'Medium',
      contact_name: request.contact_name || '',
      contact_phone: request.contact_phone || '',
      contact_email: request.contact_email || '',
      resolution_notes: request.resolution_notes || ''
    });
    setShowEditRequestModal(true);
  };

  // Save edited service request
  const saveEditedRequest = async () => {
    if (!editingRequest) return;
    
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/service-request/${editingRequest.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editRequestForm)
      });

      if (response.ok) {
        toast.success('Service request updated successfully');
        setShowEditRequestModal(false);
        setEditingRequest(null);
        fetchData();
      } else {
        toast.error('Failed to update service request');
      }
    } catch (error) {
      console.error('Error updating service request:', error);
      toast.error('Failed to update service request');
    }
  };

  // Fetch admins with permission (Super Admin only)
  const fetchAdminsWithPermission = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      
      // Fetch admins with partner dashboard permission
      const permissionsRes = await fetch(`${API_URL}/api/monitoring/partner/admins-with-permission`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (permissionsRes.ok) {
        const data = await permissionsRes.json();
        setAdminsWithPermission(data.admins || []);
      }
      
      // Fetch all monitoring users to show list of admins
      const usersRes = await fetch(`${API_URL}/api/monitoring-users/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const data = await usersRes.json();
        // Handle both array response and {users: []} response
        const users = Array.isArray(data) ? data : (data.users || []);
        // Filter to only show admins (not super_admin)
        setAllAdmins(users.filter(u => u.role === 'admin'));
      }
    } catch (error) {
      console.error('Error fetching admin permissions:', error);
    }
  };

  // Grant permission to admin
  const grantPermission = async (adminId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/permission/grant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_id: adminId })
      });

      if (response.ok) {
        toast.success('Permission granted successfully');
        fetchAdminsWithPermission();
        setSelectedAdminToGrant('');
      } else {
        toast.error('Failed to grant permission');
      }
    } catch (error) {
      console.error('Error granting permission:', error);
      toast.error('Failed to grant permission');
    }
  };

  // Revoke permission from admin
  const revokePermission = async (adminId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/partner/permission/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_id: adminId })
      });

      if (response.ok) {
        toast.success('Permission revoked successfully');
        fetchAdminsWithPermission();
      } else {
        toast.error('Failed to revoke permission');
      }
    } catch (error) {
      console.error('Error revoking permission:', error);
      toast.error('Failed to revoke permission');
    }
  };

  // Open permissions modal
  const openPermissionsModal = () => {
    fetchAdminsWithPermission();
    setShowPermissionsModal(true);
  };

  const getPartnerIcon = (partnerId) => {
    const config = PARTNER_CONFIG[partnerId];
    if (!config) return Building2;
    return config.icon;
  };

  const getPartnerConfig = (partnerId) => {
    return PARTNER_CONFIG[partnerId] || PARTNER_CONFIG.netix_fibernet;
  };

  // No access view
  if (hasAccess === false) {
    return (
      <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-testid="partner-dashboard-card">
        <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-4">
          <div className="flex items-center gap-3">
            <Lock className="h-6 w-6 text-white" />
            <div>
              <h3 className="text-white font-bold text-lg">Partner Dashboard</h3>
              <p className="text-gray-200 text-sm">Access Restricted</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 p-8 text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-gray-700 font-bold text-lg mb-2">Access Denied</h4>
          <p className="text-gray-500">
            Partner Dashboard requires Super Admin access or granted permission.
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Contact your administrator to request access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-testid="partner-dashboard-card">
      {/* Header Bar */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4">
        <div className="flex items-center justify-between">
          {/* Left side - Title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-white" />
              <div>
                <h3 className="text-white font-bold text-lg flex items-center gap-2">
                  Partner Dashboard
                  {isSuperAdmin && (
                    <span className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold">
                      Super Admin
                    </span>
                  )}
                </h3>
                <p className="text-purple-100 text-sm">Customer status by partner with service requests</p>
              </div>
            </div>
          </div>
          
          {/* Right side - Stats and controls */}
          <div className="flex items-center gap-3">
            {/* Total Customers */}
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/30">
              <span className="text-white font-bold text-lg">{summary?.total_customers || 0}</span>
              <span className="text-purple-100 text-xs ml-1">Total</span>
            </div>
            
            {/* Active */}
            <div className="bg-green-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-green-400/50 flex items-center gap-1">
              <Wifi className="h-4 w-4 text-white" />
              <span className="text-white font-bold">{summary?.total_active || 0}</span>
              <span className="text-green-100 text-xs">Active</span>
            </div>
            
            {/* Inactive */}
            <div className="bg-red-500/80 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-red-400/50 flex items-center gap-1">
              <WifiOff className="h-4 w-4 text-white" />
              <span className="text-white font-bold">{summary?.total_inactive || 0}</span>
              <span className="text-red-100 text-xs">Inactive</span>
            </div>
            
            {/* Service Request Button */}
            <Button
              size="sm"
              className="bg-white text-purple-600 hover:bg-purple-50"
              onClick={() => setShowServiceRequestModal(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Log Request
            </Button>
            
            {/* View Requests */}
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => setShowRequestsModal(true)}
            >
              <FileText className="h-4 w-4 mr-1" />
              Requests ({serviceRequests.length})
            </Button>
            
            {/* Permissions Button (Super Admin Only) */}
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={openPermissionsModal}
              >
                <Settings className="h-4 w-4 mr-1" />
                Permissions
              </Button>
            )}
            
            {/* Refresh */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Expand/Collapse */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {expanded && (
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-purple-500" />
              <span className="ml-2 text-purple-600">Loading partner data...</span>
            </div>
          ) : (
            <>
              {/* Partner Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
                {summary?.partners?.map((partner) => {
                  const config = getPartnerConfig(partner.id);
                  const Icon = config.icon;
                  const isSelected = selectedPartner === partner.id;
                  
                  return (
                    <div
                      key={partner.id}
                      className={`rounded-xl p-4 cursor-pointer transition-all duration-300 hover:scale-105 ${
                        isSelected ? 'ring-2 ring-purple-500 ring-offset-2' : ''
                      }`}
                      style={{
                        background: `linear-gradient(145deg, ${config.color}15, ${config.color}25)`,
                        border: `2px solid ${config.color}50`,
                        boxShadow: `6px 6px 12px ${config.color}20, -3px -3px 8px rgba(255,255,255,0.9)`
                      }}
                      onClick={() => setSelectedPartner(isSelected ? null : partner.id)}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-2 rounded-lg ${config.bgColor}`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <span className="font-bold text-gray-800">{partner.name}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-2xl font-bold text-gray-800">{partner.total}</p>
                          <p className="text-xs text-gray-500">Total</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-green-600">{partner.active}</p>
                          <p className="text-xs text-green-500">Active</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-600">{partner.inactive}</p>
                          <p className="text-xs text-red-500">Inactive</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Customer List by Partner */}
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {Object.entries(partnerCustomers).map(([partnerId, customers]) => {
                  if (selectedPartner && selectedPartner !== partnerId) return null;
                  if (customers.length === 0) return null;
                  
                  const config = getPartnerConfig(partnerId);
                  const Icon = config.icon;
                  
                  // Filter by search
                  const filteredCustomers = customers.filter(c => 
                    c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.ip_address?.includes(searchTerm) ||
                    c.location?.toLowerCase().includes(searchTerm.toLowerCase())
                  );
                  
                  if (filteredCustomers.length === 0) return null;
                  
                  return (
                    <div key={partnerId} className="bg-white rounded-xl p-4 shadow-md">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                        <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <span className="font-bold text-gray-800">
                          {summary?.partners?.find(p => p.id === partnerId)?.name || partnerId}
                        </span>
                        <span className="text-sm text-gray-500">({filteredCustomers.length} customers)</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {filteredCustomers.map((customer, idx) => (
                          <div
                            key={idx}
                            className={`rounded-lg p-3 border-2 transition-all ${
                              customer.is_active 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-gray-800 text-sm truncate" title={customer.customer_name}>
                                  {customer.customer_name}
                                </p>
                                <p className="text-xs text-gray-500">{customer.location}</p>
                              </div>
                              <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                                customer.is_active 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {customer.is_active ? (
                                  <>
                                    <span className="relative flex h-2 w-2">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                    </span>
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <WifiOff className="h-3 w-3" />
                                    Inactive
                                  </>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-xs text-gray-600 space-y-1">
                              <p className="font-mono">{customer.ip_address || 'N/A'}</p>
                              {customer.circuit_id && (
                                <p className="text-purple-600 font-medium">
                                  <span className="text-gray-500">Circuit:</span> {customer.circuit_id}
                                </p>
                              )}
                              {customer.ping_latency > 0 && (
                                <p className="text-green-600">{customer.ping_latency}ms ping</p>
                              )}
                            </div>
                            
                            {/* Quick Log Request Button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="w-full mt-2 h-7 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRequestForm({
                                  ...requestForm,
                                  customer_id: customer.id,
                                  customer_name: customer.customer_name,
                                  partner_id: partnerId,
                                  partner_name: summary?.partners?.find(p => p.id === partnerId)?.name || partnerId,
                                  circuit_id: customer.circuit_id || '',
                                  customer_ip: customer.ip_address || ''
                                });
                                setShowServiceRequestModal(true);
                              }}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Log Request
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Service Request Modal */}
      {showServiceRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold text-lg">Log Service Request</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowServiceRequestModal(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Customer & Partner Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={requestForm.customer_id}
                    onChange={(e) => {
                      const customer = Object.values(partnerCustomers).flat().find(c => c.id === e.target.value);
                      if (customer) {
                        // Auto-fill partner based on customer's backend_partner
                        const partnerId = customer.partner_id || '';
                        const partnerName = summary?.partners?.find(p => p.id === partnerId)?.name || customer.backend_partner || '';
                        
                        setRequestForm({
                          ...requestForm,
                          customer_id: e.target.value,
                          customer_name: customer.customer_name || '',
                          circuit_id: customer.circuit_id || '',
                          customer_ip: customer.ip_address || '',
                          partner_id: partnerId,
                          partner_name: partnerName
                        });
                      } else {
                        setRequestForm({
                          ...requestForm,
                          customer_id: e.target.value,
                          customer_name: '',
                          circuit_id: '',
                          customer_ip: '',
                          partner_id: '',
                          partner_name: ''
                        });
                      }
                    }}
                  >
                    <option value="">Select Customer</option>
                    {Object.values(partnerCustomers).flat().map((c, idx) => (
                      <option key={idx} value={c.id}>{c.customer_name} {c.circuit_id ? `(${c.circuit_id})` : ''}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner</label>
                  <div className="w-full border rounded-lg p-2 text-sm bg-gray-50 text-gray-700">
                    {requestForm.partner_name || 'Auto-filled from customer'}
                  </div>
                  <input type="hidden" value={requestForm.partner_id} />
                  {/* Notification indicator for Timbl */}
                  {requestForm.partner_id === 'timbl' && (
                    <div className="mt-2 flex items-center gap-2 text-xs bg-green-50 text-green-700 p-2 rounded-lg border border-green-200">
                      <Mail className="h-3.5 w-3.5" />
                      <span>Email notification will be sent to Timbl NOC team</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Circuit ID & Customer IP (Read-only when selected from customer) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Circuit ID</label>
                  <Input
                    placeholder="Circuit ID"
                    value={requestForm.circuit_id}
                    onChange={(e) => setRequestForm({ ...requestForm, circuit_id: e.target.value })}
                    className={requestForm.circuit_id ? 'bg-purple-50 border-purple-200' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer IP</label>
                  <Input
                    placeholder="Customer IP Address"
                    value={requestForm.customer_ip}
                    onChange={(e) => setRequestForm({ ...requestForm, customer_ip: e.target.value })}
                    className={requestForm.customer_ip ? 'bg-blue-50 border-blue-200' : ''}
                  />
                </div>
              </div>
              
              {/* Request Type & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={requestForm.request_type}
                    onChange={(e) => setRequestForm({ ...requestForm, request_type: e.target.value })}
                  >
                    <option value="General">General</option>
                    <option value="Link Down">Link Down</option>
                    <option value="Connectivity Issue">Connectivity Issue</option>
                    <option value="Speed Issue">Speed Issue</option>
                    <option value="Hardware Issue">Hardware Issue</option>
                    <option value="Billing">Billing</option>
                    <option value="New Installation">New Installation</option>
                    <option value="Upgrade">Upgrade</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={requestForm.priority}
                    onChange={(e) => setRequestForm({ ...requestForm, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              
              {/* First Troubleshoot */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Troubleshoot Done?</label>
                <select
                  className="w-full border rounded-lg p-2 text-sm"
                  value={requestForm.first_troubleshoot}
                  onChange={(e) => setRequestForm({ ...requestForm, first_troubleshoot: e.target.value })}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
                {requestForm.first_troubleshoot === 'No' && (
                  <p className="text-xs text-amber-600 mt-1">⚠️ Please perform initial troubleshooting before logging request</p>
                )}
              </div>
              
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <Input
                  placeholder="Brief description of the issue"
                  value={requestForm.subject}
                  onChange={(e) => setRequestForm({ ...requestForm, subject: e.target.value })}
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm min-h-[100px]"
                  placeholder="Detailed description of the issue..."
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                />
              </div>
              
              {/* Local Contact Person */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Local Contact Person</label>
                <Input
                  placeholder="Name of local contact person"
                  value={requestForm.contact_name}
                  onChange={(e) => setRequestForm({ ...requestForm, contact_name: e.target.value })}
                />
              </div>
              
              {/* Submit Button */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowServiceRequestModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-gradient-to-r from-indigo-600 to-purple-600"
                  onClick={submitServiceRequest}
                  disabled={!requestForm.customer_id || !requestForm.partner_id || !requestForm.subject}
                >
                  Submit Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Requests List Modal */}
      {showRequestsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-xl sticky top-0">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold text-lg">Service Requests</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowRequestsModal(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {serviceRequests.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No service requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {serviceRequests.map((request, idx) => (
                    <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      {/* Ticket Number Header */}
                      {request.ticket_number && (
                        <div className="flex items-center gap-2 mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-2 rounded-lg">
                          <FileText className="h-4 w-4" />
                          <span className="font-mono font-bold text-sm tracking-wider">{request.ticket_number}</span>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-bold text-gray-800">{request.subject}</h4>
                          <p className="text-sm text-gray-500">
                            {request.customer_name} • {request.partner_name}
                            {request.circuit_id && (
                              <span className="ml-2 text-purple-600 font-medium">
                                Circuit: {request.circuit_id}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            request.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                            request.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                            request.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {request.priority}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            request.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                            request.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {request.status}
                          </span>
                        </div>
                      </div>
                      
                      {/* Downtime Tracker */}
                      <div className={`flex items-center gap-2 mb-3 p-2 rounded-lg ${
                        request.status === 'Resolved' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200 animate-pulse'
                      }`}>
                        <Timer className={`h-4 w-4 ${request.status === 'Resolved' ? 'text-green-600' : 'text-red-600'}`} />
                        <span className={`text-sm font-semibold ${request.status === 'Resolved' ? 'text-green-700' : 'text-red-700'}`}>
                          {request.status === 'Resolved' ? 'Total Downtime: ' : 'Downtime: '}
                          {calculateDowntime(request.created_at, request.resolved_at)}
                        </span>
                        {request.status !== 'Resolved' && (
                          <span className="text-xs text-red-500 ml-1">(ongoing)</span>
                        )}
                      </div>
                      
                      {/* Circuit ID and IP Info */}
                      {(request.circuit_id || request.customer_ip) && (
                        <div className="flex items-center gap-4 text-xs mb-2 bg-purple-50 rounded p-2 border border-purple-200">
                          {request.circuit_id && (
                            <span className="flex items-center gap-1 text-purple-700 font-medium">
                              <Zap className="h-3 w-3" />
                              Circuit: {request.circuit_id}
                            </span>
                          )}
                          {request.customer_ip && (
                            <span className="flex items-center gap-1 text-blue-700 font-mono">
                              <Globe className="h-3 w-3" />
                              IP: {request.customer_ip}
                            </span>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm text-gray-600 mb-2">{request.description}</p>
                      
                      {/* Contact Info */}
                      {request.contact_name && (
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-2 bg-gray-50 rounded p-2">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            <span className="text-gray-400">Local Contact:</span> {request.contact_name}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(request.created_at).toLocaleString()}
                          </span>
                          <span>Type: {request.request_type}</span>
                          <span>By: {request.created_by}</span>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          {/* Edit Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openEditRequestModal(request)}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          
                          {/* Status Update Buttons */}
                          {request.status !== 'Resolved' && (
                            <>
                              {request.status === 'Open' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => updateServiceRequestStatus(request.id, 'In Progress')}
                                  disabled={updatingRequest === request.id}
                                >
                                  {updatingRequest === request.id ? (
                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                  ) : (
                                    'In Progress'
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-green-600 hover:bg-green-700"
                                onClick={() => updateServiceRequestStatus(request.id, 'Resolved')}
                                disabled={updatingRequest === request.id}
                              >
                                {updatingRequest === request.id ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Resolve
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                          
                          {/* Reminder Button - Only for non-resolved requests */}
                          {request.status !== 'Resolved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700 border-orange-200"
                              onClick={() => sendReminder(request)}
                              disabled={sendingReminder === request.id}
                            >
                              {sendingReminder === request.id ? (
                                <RefreshCw className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <Bell className="h-3 w-3 mr-1" />
                                  Reminder
                                </>
                              )}
                            </Button>
                          )}
                          
                          {/* Delete Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                            onClick={() => deleteServiceRequest(request.id, request.ticket_number)}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Request Modal */}
      {showEditRequestModal && editingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-bold text-lg">Edit Service Request</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => {
                    setShowEditRequestModal(false);
                    setEditingRequest(null);
                  }}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Customer Info (read-only) */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700">
                  Customer: {editingRequest.customer_name} • {editingRequest.partner_name}
                </p>
                {editingRequest.circuit_id && (
                  <p className="text-xs text-purple-600">Circuit ID: {editingRequest.circuit_id}</p>
                )}
              </div>
              
              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <Input
                  value={editRequestForm.subject}
                  onChange={(e) => setEditRequestForm({ ...editRequestForm, subject: e.target.value })}
                  placeholder="Subject"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm min-h-[80px]"
                  value={editRequestForm.description}
                  onChange={(e) => setEditRequestForm({ ...editRequestForm, description: e.target.value })}
                  placeholder="Description"
                />
              </div>
              
              {/* Request Type & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={editRequestForm.request_type}
                    onChange={(e) => setEditRequestForm({ ...editRequestForm, request_type: e.target.value })}
                  >
                    <option value="General">General</option>
                    <option value="Connectivity Issue">Connectivity Issue</option>
                    <option value="Speed Issue">Speed Issue</option>
                    <option value="Hardware Issue">Hardware Issue</option>
                    <option value="Billing">Billing</option>
                    <option value="New Installation">New Installation</option>
                    <option value="Upgrade">Upgrade</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm"
                    value={editRequestForm.priority}
                    onChange={(e) => setEditRequestForm({ ...editRequestForm, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local Contact Person</label>
                  <Input
                    value={editRequestForm.contact_name}
                    onChange={(e) => setEditRequestForm({ ...editRequestForm, contact_name: e.target.value })}
                    placeholder="Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                  <Input
                    value={editRequestForm.contact_phone}
                    onChange={(e) => setEditRequestForm({ ...editRequestForm, contact_phone: e.target.value })}
                    placeholder="Phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <Input
                    value={editRequestForm.contact_email}
                    onChange={(e) => setEditRequestForm({ ...editRequestForm, contact_email: e.target.value })}
                    placeholder="Email"
                  />
                </div>
              </div>
              
              {/* Resolution Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes</label>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm min-h-[60px]"
                  value={editRequestForm.resolution_notes}
                  onChange={(e) => setEditRequestForm({ ...editRequestForm, resolution_notes: e.target.value })}
                  placeholder="Add notes about resolution or updates..."
                />
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditRequestModal(false);
                    setEditingRequest(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={saveEditedRequest}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Management Modal (Super Admin Only) */}
      {showPermissionsModal && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-4 rounded-t-xl sticky top-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-white" />
                  <h3 className="text-white font-bold text-lg">Permission Management</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowPermissionsModal(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Grant Permission Section */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-green-600" />
                  Grant Access to Admin
                </h4>
                <div className="flex gap-2">
                  <select
                    className="flex-1 border rounded-lg p-2 text-sm"
                    value={selectedAdminToGrant}
                    onChange={(e) => setSelectedAdminToGrant(e.target.value)}
                  >
                    <option value="">Select Admin to Grant Access</option>
                    {allAdmins
                      .filter(admin => !adminsWithPermission.some(p => p.user_id === admin.id))
                      .map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.username} ({admin.email})
                        </option>
                      ))
                    }
                  </select>
                  <Button
                    onClick={() => selectedAdminToGrant && grantPermission(selectedAdminToGrant)}
                    disabled={!selectedAdminToGrant}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <UserCheck className="h-4 w-4 mr-1" />
                    Grant
                  </Button>
                </div>
              </div>
              
              {/* Current Permissions */}
              <div>
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" />
                  Admins with Partner Dashboard Access
                </h4>
                
                {adminsWithPermission.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Shield className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No admins have been granted access yet</p>
                    <p className="text-gray-400 text-sm">Use the form above to grant access</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adminsWithPermission.map((permission, idx) => {
                      const admin = allAdmins.find(a => a.id === permission.user_id);
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-800">
                              {admin?.username || permission.user_id}
                            </p>
                            <p className="text-xs text-gray-500">
                              Granted by: {permission.granted_by} • {new Date(permission.granted_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => revokePermission(permission.user_id)}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Revoke
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Info Box */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">About Partner Dashboard Permissions</p>
                    <ul className="mt-1 space-y-1 text-blue-600">
                      <li>• Super Admins always have full access</li>
                      <li>• Regular Admins need explicit permission to view this dashboard</li>
                      <li>• Granted admins can view all partner data and log service requests</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;
